import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Palette, Sparkles, ArrowRight, Clock, ListChecks, Lightbulb, Wrench, Star } from "lucide-react";
import { Button } from "../components/ui/button";
import CTA from "../components/sections/CTA";
import { craftsPool, craftCategories } from "../mock/crafts";

const difficultyColor = {
  Easy: "bg-emerald-100 text-emerald-800",
  Medium: "bg-amber-100 text-amber-800",
  Hard: "bg-rose-100 text-rose-800"
};

const LittleMindCraftWorkPage = () => {
  const [activeCategory, setActiveCategory] = useState("All");
  const [openId, setOpenId] = useState(null);

  const filtered = useMemo(
    () => (activeCategory === "All" ? craftsPool : craftsPool.filter((c) => c.category === activeCategory)),
    [activeCategory]
  );

  return (
    <div>
      {/* Hero */}
      <section className="pt-16 pb-14 bg-gradient-to-br from-rose-50 via-orange-50 to-amber-50 relative overflow-hidden" data-testid="crafts-hero">
        <div className="absolute inset-0 opacity-10 pointer-events-none">
          <div className="absolute top-10 left-10 w-52 h-52 border-2 border-rose-400 rounded-full" />
          <div className="absolute bottom-10 right-10 w-72 h-72 border-2 border-orange-400 rounded-full" />
        </div>
        <div className="container mx-auto px-4 text-center relative z-10">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white shadow-sm border border-orange-100 mb-4">
            <Palette className="w-4 h-4 text-rose-600" />
            <span className="text-xs font-semibold text-rose-700 tracking-widest uppercase">Little Mind Craft Work</span>
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-stone-800 mb-4">
            Tiny Hands, <span className="text-orange-600">Big Creations</span>
          </h1>
          <p className="font-devanagari text-2xl text-orange-700 mb-4">छोटे हाथ, बड़ी कल्पना</p>
          <p className="text-stone-600 max-w-2xl mx-auto text-base">
            Thirty joyful craft ideas rooted in Bharatiya values and everyday materials. Roll up those sleeves, grab a paper plate, and let your little one create magic with mummy or papa.
          </p>
          <div className="mt-8 max-w-4xl mx-auto rounded-3xl overflow-hidden shadow-2xl border-4 border-white">
            <img
              src="https://customer-assets-rejwkqb3.emergentagent.net/job_gayanam-preschool/artifacts/nmgcj8u6_8944c4cc-e0b1-4f8d-9c7f-aa9b5ca5f73a.png"
              alt="Aadi Gayanam Play School — We Love to Create"
              className="w-full h-auto"
              data-testid="crafts-hero-image"
            />
          </div>
        </div>
      </section>

      {/* Category filter */}
      <section className="py-8 bg-white border-b border-orange-100 sticky top-[80px] z-30" data-testid="crafts-filter">
        <div className="container mx-auto px-4">
          <div className="flex flex-wrap gap-2 justify-center">
            {craftCategories.map((c) => (
              <button
                key={c}
                onClick={() => { setActiveCategory(c); setOpenId(null); }}
                className={`px-4 py-1.5 rounded-full text-xs font-semibold border transition-all ${
                  activeCategory === c
                    ? "bg-orange-600 text-white border-orange-600"
                    : "bg-white text-stone-700 border-stone-200 hover:border-orange-300"
                }`}
                data-testid={`filter-${c.toLowerCase()}`}
              >
                {c}
              </button>
            ))}
          </div>
          <p className="text-center text-xs text-stone-500 mt-3">
            Showing <span className="font-bold text-orange-700">{filtered.length}</span> of {craftsPool.length} crafts
          </p>
        </div>
      </section>

      {/* Crafts grid */}
      <section className="py-14 bg-gradient-to-b from-white via-amber-50/30 to-white" data-testid="crafts-grid">
        <div className="container mx-auto px-4">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map((craft) => {
              const isOpen = openId === craft.id;
              return (
                <article
                  key={craft.id}
                  className={`group rounded-3xl bg-white border border-orange-100 overflow-hidden shadow-md hover:shadow-2xl transition-all duration-500 ${isOpen ? "lg:col-span-2" : "hover:-translate-y-1"}`}
                  data-testid={`craft-card-${craft.id}`}
                >
                  {/* Illustrated banner per craft — unique per card */}
                  <div className={`relative h-44 bg-gradient-to-br ${craft.palette} text-white overflow-hidden`}>
                    {/* Decorative Bharatiya-style rings */}
                    <div className="absolute inset-0 opacity-20 pointer-events-none">
                      <div className="absolute -top-8 -left-8 w-40 h-40 border-2 border-white rounded-full" />
                      <div className="absolute -top-2 -left-2 w-28 h-28 border border-white rounded-full" />
                      <div className="absolute -bottom-12 -right-10 w-52 h-52 border-2 border-white rounded-full" />
                      <div className="absolute -bottom-4 -right-2 w-32 h-32 border border-white rounded-full" />
                    </div>
                    {/* Dotted mandala pattern (SVG) */}
                    <svg className="absolute inset-0 w-full h-full opacity-15 pointer-events-none" xmlns="http://www.w3.org/2000/svg">
                      <defs>
                        <pattern id={`dots-${craft.id}`} x="0" y="0" width="24" height="24" patternUnits="userSpaceOnUse">
                          <circle cx="4" cy="4" r="1.5" fill="white" />
                        </pattern>
                      </defs>
                      <rect width="100%" height="100%" fill={`url(#dots-${craft.id})`} />
                    </svg>
                    {/* Giant emoji as the illustration */}
                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                      <span className="text-8xl drop-shadow-2xl transition-transform duration-500 group-hover:scale-110">
                        {craft.icon}
                      </span>
                    </div>
                    {/* Bottom badges */}
                    <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/50 to-transparent">
                      <span className="inline-block bg-white/25 backdrop-blur-sm text-white text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider mb-1">
                        Craft #{craft.id} • {craft.category}
                      </span>
                      <h3 className="font-display text-lg font-bold leading-tight text-white drop-shadow">{craft.title}</h3>
                    </div>
                  </div>

                  {/* Meta */}
                  <div className="px-5 py-3 flex items-center gap-2 border-b border-orange-50 flex-wrap">
                    <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${difficultyColor[craft.difficulty] || difficultyColor.Easy}`}>
                      <Star className="w-3 h-3" /> {craft.difficulty}
                    </span>
                    <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-orange-100 text-orange-800">
                      <Clock className="w-3 h-3" /> {craft.time}
                    </span>
                  </div>

                  {/* Body */}
                  <div className="p-5">
                    <div className="mb-4">
                      <div className="flex items-center gap-2 mb-2">
                        <Wrench className="w-4 h-4 text-orange-600" />
                        <p className="text-xs font-bold text-orange-700 uppercase tracking-wider">Materials</p>
                      </div>
                      <div className="flex flex-wrap gap-1.5">
                        {craft.materials.map((m) => (
                          <span key={m} className="text-[11px] bg-stone-100 text-stone-700 px-2 py-0.5 rounded-full">
                            {m}
                          </span>
                        ))}
                      </div>
                    </div>

                    {!isOpen ? (
                      <button
                        onClick={() => setOpenId(craft.id)}
                        className="w-full text-center inline-flex items-center justify-center gap-1 text-sm font-bold text-orange-700 hover:text-orange-900 transition-all py-2"
                        data-testid={`craft-open-${craft.id}`}
                      >
                        See how to make it <ArrowRight className="w-4 h-4" />
                      </button>
                    ) : (
                      <>
                        <div className="mb-4">
                          <div className="flex items-center gap-2 mb-2">
                            <ListChecks className="w-4 h-4 text-orange-600" />
                            <p className="text-xs font-bold text-orange-700 uppercase tracking-wider">Steps</p>
                          </div>
                          <ol className="space-y-1.5">
                            {craft.steps.map((s, i) => (
                              <li key={s} className="flex gap-2 text-sm text-stone-700 leading-relaxed">
                                <span className="flex-shrink-0 w-5 h-5 rounded-full bg-orange-500 text-white text-[10px] font-bold flex items-center justify-center mt-0.5">{i + 1}</span>
                                <span>{s}</span>
                              </li>
                            ))}
                          </ol>
                        </div>
                        <div className="border-l-4 border-orange-500 pl-3 py-1 bg-orange-50/70 rounded">
                          <div className="flex items-center gap-1.5 mb-0.5">
                            <Lightbulb className="w-3 h-3 text-orange-600" />
                            <p className="text-[10px] font-bold text-orange-700 uppercase tracking-wider">What we learn</p>
                          </div>
                          <p className="text-sm text-stone-800 font-medium">{craft.learn}</p>
                        </div>
                        <button
                          onClick={() => setOpenId(null)}
                          className="mt-4 text-xs font-bold text-stone-500 hover:text-orange-700"
                          data-testid={`craft-close-${craft.id}`}
                        >
                          ↑ Hide steps
                        </button>
                      </>
                    )}
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {/* Closing card */}
      <section className="py-14 bg-white">
        <div className="container mx-auto px-4 max-w-3xl">
          <div className="p-8 md:p-10 bg-gradient-to-br from-orange-950 via-red-950 to-amber-950 text-white rounded-3xl shadow-xl text-center relative overflow-hidden">
            <div className="absolute inset-0 opacity-10 pointer-events-none">
              <div className="absolute top-2 left-2 w-40 h-40 border-2 border-yellow-300 rounded-full" />
              <div className="absolute bottom-2 right-2 w-52 h-52 border-2 border-yellow-300 rounded-full" />
            </div>
            <div className="relative z-10">
              <Sparkles className="w-8 h-8 text-yellow-200 mx-auto mb-3" />
              <p className="font-devanagari text-2xl text-yellow-100 leading-relaxed mb-3">
                करके सीखो, हँसकर सीखो।
              </p>
              <p className="text-orange-100 italic max-w-2xl mx-auto text-sm md:text-base">
                Learn by doing, learn by laughing. Every little craft is a small victory that builds a big, confident child.
              </p>
            </div>
          </div>

          <div className="mt-10 text-center">
            <Link to="/admission">
              <Button className="bg-gradient-to-r from-orange-600 to-red-600 text-white rounded-full px-8 h-11 btn-shine" data-testid="crafts-cta">
                Let Your Child Craft Their Future <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <CTA />
    </div>
  );
};

export default LittleMindCraftWorkPage;
